import semver from 'semver';

/**
 * Class to store information about Salesforce packages and contain logic for version comparison.
 */
export class SFPackageVersion {
    majorVersion : number = 0;
    minorVersion : number = 0;
    patchVersion : number = 0;
    ignorePatchLevel : boolean = false;

    toString() {
        return `${this.majorVersion}.${this.minorVersion}.${this.patchVersion}`;
    }

    isNewerThan(versionToCompareWith: SFPackageVersion) {
        let higherMajorVersion = this.majorVersion > versionToCompareWith.majorVersion;
        let sameMajorVersion = this.majorVersion === versionToCompareWith.majorVersion;
        let higherMinorVersion = sameMajorVersion && this.minorVersion > versionToCompareWith.minorVersion;
        let sameMinorVersion = this.minorVersion === versionToCompareWith.minorVersion;
        let higherPatchVersion = sameMajorVersion && sameMinorVersion && this.patchVersion > versionToCompareWith.patchVersion;

        return higherMajorVersion || higherMinorVersion || higherPatchVersion;
    }

    isSameAs(versionToCompareWith: SFPackageVersion) {
        return this.majorVersion === versionToCompareWith.majorVersion &&
               this.minorVersion === versionToCompareWith.minorVersion &&
               (this.ignorePatchLevel ||  this.patchVersion === versionToCompareWith.patchVersion);
    }

    isOlderThan(versionToCompareWith: SFPackageVersion) {
        return !this.isNewerThan(versionToCompareWith) && !this.isSameAs(versionToCompareWith);
    }

    /**
     * Checks if a given Salesforce package starts with a specific dependent extension within a version range.
     *
     * @param pkg - The Salesforce package to check.
     * @param dependentExtension - The dependent extension to check for.
     * @param from - The minimum version range to check against, in the format "major.minor.patch". Null values are treated as wildcards.
     * @param to - The maximum version range to check against, in the format "major.minor.patch". Null values are treated as wildcards.
     * @returns True if the package starts with the dependent extension and is within the specified version range, false otherwise.
     * 
     * use cases
     * 1. isWithinRange(pkg, 'ComplianceQuest', '14.0.0', '14.5.0') - checks if the package is 'ComplianceQuest' and is between versions 14.0.0 and 14.5.0
     * 2. isWithinRange(pkg, 'ComplianceQuest', '13.0.0') - checks if the package is 'ComplianceQuest' and is at least version 13.0.0
     * 3. isWithinRange(pkg, 'ComplianceQuest', null, '14.5.0') - checks if the package is 'ComplianceQuest' and is at most version 14.5.0
     */
    isWithinRange(pkg: SFPackage, dependentExtension: string, from?: string, to?: string): boolean {
        if (pkg.name !== dependentExtension) return false;

        const versionStr = `${pkg.version.majorVersion}.${pkg.version.minorVersion}.${pkg.version.patchVersion}`;
        let range = '';

        if (from && to) {
            range = `>=${from} <=${to}`;
        } else if (from) {
            range = `>=${from}`;
        } else if (to) {
            range = `<=${to}`;
        } else {
            return pkg.name === dependentExtension;
        }

        return semver.satisfies(versionStr, range);
    }
    constructor(majorVersion: number, minorVersion: number, patchVersion: number) {
        this.majorVersion = majorVersion;
        this.minorVersion = minorVersion;
        this.patchVersion = patchVersion;
    }
}

/**
 * Represents a salesforce app in an org
 */
export class SFPackage {
    name : string;
    version : SFPackageVersion;
    firstInstalledPackageId ?: string;

    constructor(name: string, version : SFPackageVersion, firstInstalledPackageId?: string) {
        this.name = name;
        this.version = version;
        this.firstInstalledPackageId = firstInstalledPackageId;
    }
}

/**
 * Parses a version string into an `SFPackageVersion` object.
 *
 * @param version - The version string to parse, in the format "major.minor.patch".
 * @returns An `SFPackageVersion` object representing the parsed version.
 */
export function parseVersion(version: string): SFPackageVersion {
    const versionParts = version.split('.').map((item: string) => {
        if (item.toLowerCase() === 'x') {
            return 0;
        }
        const parsed = parseInt(item, 10);
        return Number.isNaN(parsed) ? 0 : parsed;
    });
    return new SFPackageVersion(versionParts[0] ?? 0, versionParts[1] ?? 0, versionParts[2] ?? 0);
}
